import createCachedSelector from 're-reselect';
import {isSameDay, parseISO} from 'date-fns';
import {
  getConnectedUserEntities,
  getWorkdaysForConnectionsByDate,
  getWorkspaceReservationsForConnectionsByDate,
} from '../../data/connections';
import {RootState} from '../../rootReducer';
import {getDefaultBuildingId} from '../../data/buildings';
import {getWorkspaceReservationByDate} from '../../data/workspaceReservation';
import {getStartOfTodayISOString} from '../../data/settings';
import {getIsCheckInFeatureEnabled, getIsManualCheckInFeatureEnabled} from '../../infrastructure/features';
import {getUserCheckInStatus} from '../../data/checkIn';
import {CheckInEventType} from '@lib/infrastructure';
import {getCalendar} from '../calendar';
import {getWorkdayByDate} from '../../data/workday';
import {groupConnectionsByWorkdayBuilding} from './utils';

const getConnectionReservationInfo = createCachedSelector(
  [
    getWorkdaysForConnectionsByDate,
    (state: RootState, dateString: string) => getWorkspaceReservationsForConnectionsByDate(state, {date: dateString}),
    (state: RootState, dateString: string) => getWorkspaceReservationByDate(state, parseISO(dateString)),
  ],
  (connectionWorkdays, connectionWorkspaceReservations, loggedInUserWorkspaceReservation) => {
    return {
      connectionWorkdays,
      connectionWorkspaceReservations,
      loggedInUserWorkspaceReservation,
    };
  },
)((_state: RootState, dateString: string) => dateString);

export const getConnectionsGroupedByWorkdayBuilding = createCachedSelector(
  getDefaultBuildingId,
  getConnectedUserEntities,
  (_state: RootState, date: string) => date,
  getConnectionReservationInfo,
  (
    defaultBuildingId,
    connectedUsers,
    _date,
    {connectionWorkspaceReservations, loggedInUserWorkspaceReservation, connectionWorkdays},
  ) => {
    return groupConnectionsByWorkdayBuilding(
      defaultBuildingId,
      connectedUsers,
      connectionWorkspaceReservations,
      loggedInUserWorkspaceReservation,
      connectionWorkdays,
    );
  },
)((_state: RootState, date) => date);

export const getCanShowCheckInTile = createCachedSelector(
  getIsCheckInFeatureEnabled,
  getIsManualCheckInFeatureEnabled,
  getStartOfTodayISOString,
  getUserCheckInStatus,
  (state: RootState, selectedDate: string) => getWorkdayByDate(state, selectedDate),
  (_state: RootState, selectedDate: string) => selectedDate,
  (isCheckInFeatureEnabled, isManualCheckInFeatureEnabled, todayISOString, {eventType}, workday, selectedDate) => {
    const isSelectedDateToday = isSameDay(parseISO(selectedDate), parseISO(todayISOString));
    const isOfficeDay = workday?.status === 'OfficeDay';
    const hasCheckInStatus = eventType !== CheckInEventType.NotCheckedInYet;
    return (
      isSelectedDateToday &&
      ((isManualCheckInFeatureEnabled && (isOfficeDay || hasCheckInStatus)) ||
        (isCheckInFeatureEnabled && hasCheckInStatus))
    );
  },
)((_state, selectedDate) => selectedDate);

export const checkInRelevant = (state: RootState) => {
  const manualEnabled = getIsManualCheckInFeatureEnabled(state);
  if (manualEnabled) return true;

  const autoEnabled = getIsCheckInFeatureEnabled(state);
  if (autoEnabled) {
    const status = getUserCheckInStatus(state);

    // With automatic check in, users can only check out
    return status.eventType !== CheckInEventType.NotCheckedInYet;
  }

  return false;
};

export const getSectionIndexOfSelectedDate = createCachedSelector(
  getCalendar,
  (_state: RootState, selectedDate: string) => selectedDate,
  (calendarWeeks, selectedDate) => {
    const calendarWeekOfSelectedDate = calendarWeeks.find((k) => k.data.find((v) => v.date.includes(selectedDate)));
    const sectionIndex = calendarWeekOfSelectedDate && calendarWeeks.indexOf(calendarWeekOfSelectedDate);

    return sectionIndex ?? 0;
  },
)((_state, selectedDate) => selectedDate);
